import { getRoot } from 'mobx-easy';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import i18next from 'i18next';
import { ActionType, AlertDisplayCategory, AlertSeverity, ConsoleAction, UserRole } from '@bringg/types';
import { Notification } from '@bringg/react-components';
import { promiseMemoOne } from '@bringg-frontend/utils';

import RootStore from 'bringg-web/stores/root-store';
import { workflowsRootStore } from '../internal';
import { workflowsProvider } from '../../data-providers/workflows-provider';

interface AutomationAlert {
	id: number;
	severity: AlertSeverity;
	title: string;
	description_rich_text?: string;
	display_category?: AlertDisplayCategory;
	role: UserRole;
}

class WorkflowAlertsStore {
	loaded = false;

	constructor() {
		makeObservable(this, {
			loaded: observable,
			load: action,
			alertTypes: computed,
			alerts: computed,
			configurationsFilteredByUserRole: computed,
			configurationsByDisplayCategory: computed
		});

		void this.load();
	}

	load = promiseMemoOne(async () => {
		try {
			await workflowsProvider.loadAll();
		} catch (e) {
			console.error('failed to load alerts configurations', e);
			Notification.error(i18next.t('ALERTS_CONFIGURATION.FAILED_TO_LOAD_SETS'));
		} finally {
			runInAction(() => {
				this.loaded = true;
			});
		}
	});

	get configurationIdsFilteredByUserRole(): Set<number> {
		const filteredByNotifiers = this.configurationsFilteredByUserRole.map(conf => conf.id);

		return new Set(filteredByNotifiers);
	}

	get configurationsFilteredByUserRole(): AutomationAlert[] {
		const { isAdmin, isDispatcher } = getRoot<RootStore>().data.usersStore;

		return this.alerts.filter(
			alert => (alert.role === UserRole.Admin && isAdmin) || (alert.role === UserRole.Dispatcher && isDispatcher)
		);
	}

	get configurationsByDisplayCategory(): Record<AlertDisplayCategory, AutomationAlert[]> {
		return this.configurationsFilteredByUserRole.reduce<Record<AlertDisplayCategory, AutomationAlert[]>>(
			(acc, alert) => {
				if (alert.display_category && acc[alert.display_category]) {
					acc[alert.display_category].push(alert);
				}
				return acc;
			},
			{
				[AlertDisplayCategory.TASK]: [],
				[AlertDisplayCategory.DRIVER]: [],
				[AlertDisplayCategory.OPTIMIZATION]: []
			}
		);
	}

	get alerts(): AutomationAlert[] {
		const workflows = workflowsRootStore.getStore().workflowRepo.getAll();

		return workflows
			.map(workflow => ({
				id: workflow.id,
				workflowTitle: workflow.title,
				enabled: workflow.enabled,
				notifyActions: workflow.actions.flatActions.filter(
					a => a.action_type === ActionType.ALERT
				) as ConsoleAction[]
			}))
			.filter(({ enabled, notifyActions }) => enabled && notifyActions.length)
			.map(({ id, workflowTitle, notifyActions }) => {
				const actionWithDataType = notifyActions.filter(action => action.data.type);
				const actions = actionWithDataType.length ? actionWithDataType : notifyActions;
				return actions.map(action => ({
					id: action.data.type || id,
					severity: action.data.severity ?? AlertSeverity.Medium,
					title: action.data.title || workflowTitle,
					display_category: action.data.display_category,
					description_rich_text: action.data.description_rich_text,
					role: action.data.role
				}));
			})
			.flat();
	}

	get alertTypes(): number[] {
		return this.alerts.map(({ id }) => id);
	}

	getAlertById(id: number): AutomationAlert | undefined {
		return this.alerts.find(alert => alert.id === id);
	}
}

export default WorkflowAlertsStore;
